import { useState, useEffect } from 'react';
import { useLocalStorage, useSessionStorage } from "./useLocalStorage";

const useAuth = () => {
    // Use state to track session storage values
    const [dealer, setDealer] = useState(() => {
        const stored = sessionStorage.getItem('dealer');
        return stored ? JSON.parse(stored) : null;
    });
    
    const [dealerToken, setDealerToken] = useState(() => {
        return sessionStorage.getItem('dealerToken') || null;
    });
    
    const [user, setUser] = useState(() => {
        const stored = sessionStorage.getItem('user');
        return stored ? JSON.parse(stored) : null;
    });
    
    const [token, setToken] = useState(() => {
        return sessionStorage.getItem('token') || null;
    });

    // Listen for storage changes
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'dealer') {
                setDealer(e.newValue ? e.newValue : null);
            } else if (e.key === 'dealerToken') {
                setDealerToken(e.newValue);
            } else if (e.key === 'user') {
                console.log("User changed", e.newValue);
                setUser(e.newValue ? e.newValue : null);
            } else if (e.key === 'token') {
                setToken(e.newValue);
            }
        };

        // Listen for storage events from other tabs/windows
        window.addEventListener('storage', handleStorageChange);

        // Custom event listener for same-tab storage changes
        const handleCustomStorageChange = (e) => {
            handleStorageChange(e.detail);
        };
        window.addEventListener('sessionStorageChange', handleCustomStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('sessionStorageChange', handleCustomStorageChange);
        };
    }, []);

    // Helper function to update session storage and trigger state update
    const updateSessionStorage = (key, value) => {
        if (value === null) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, typeof value === 'object' ? JSON.stringify(value) : value);
        }
        
        // Trigger custom event for same-tab updates
        window.dispatchEvent(new CustomEvent('sessionStorageChange', {
            detail: { key, newValue: value }
        }));
    };

    const isAuthenticated = !!dealer && !!dealerToken && !!user && !!token;
    const isDealerAuthenticated = !!dealer && !!dealerToken;
    const isUserAuthenticated = !!user && !!token;
    const isDealer = !!dealer;
    const isUser = !!user;

    const logout = async () => {
        updateSessionStorage('role', null);
        updateSessionStorage('token', null);
        updateSessionStorage('user', null);
        updateSessionStorage('userId', null);
        updateSessionStorage('dealer', null);
        updateSessionStorage('dealerToken', null);
        window.location.href = '/';
    };

    // Helper functions to update auth state
    const setUserAuth = (userData, userToken) => {
        updateSessionStorage('user', userData);
        updateSessionStorage('token', userToken);
        updateSessionStorage('role', userData?.role || 'user');
        updateSessionStorage('userId', userData?._id || null);
    };

    const setDealerAuth = (dealerData, dealerToken) => {
        updateSessionStorage('dealer', dealerData);
        updateSessionStorage('dealerToken', dealerToken);
        updateSessionStorage('role', 'dealer');
    };

    const clearAuth = () => {
        updateSessionStorage('role', null);
        updateSessionStorage('token', null);
        updateSessionStorage('user', null);
        updateSessionStorage('userId', null);
        updateSessionStorage('dealer', null);
        updateSessionStorage('dealerToken', null);
    };

    return {
        logout,
        isAuthenticated,
        dealer,
        dealerToken,
        user,
        token,
        isDealerAuthenticated,
        isUserAuthenticated,
        isDealer,
        isUser,
        setUserAuth,
        setDealerAuth,
        clearAuth,
        updateSessionStorage
    };
};

export default useAuth;